import type { ComponentType, ReactNode } from "react"
import React, { createContext, forwardRef, useContext } from "react"

export interface UiRouter {
  pathname: string
  query: Record<string, string | string[]>
  push: (url: string) => void
  replace: (url: string) => void
  back: () => void
  forward: () => void
}

export interface UiImageProps {
  src: string
  alt: string
  width?: number
  height?: number
  className?: string
  priority?: boolean
  placeholder?: "blur" | "empty"
  blurDataURL?: string
  onLoad?: () => void
  onError?: () => void
  [key: string]: any
}

// keep href a string for portability; adapters can widen it
export interface UiLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string
  children?: ReactNode
}

export interface NavigationContextType {
  Link: ComponentType<UiLinkProps>
  Image: ComponentType<UiImageProps>
  useRouter: () => UiRouter
  useIsActive: (path: string, opts?: { exact?: boolean }) => boolean
}

const NavigationContext = createContext<NavigationContextType | null>(null)

export function NavigationProvider({ children, value }: { children: ReactNode; value: NavigationContextType }) {
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}

export function useNavigation(): NavigationContextType {
  const ctx = useContext(NavigationContext)
  if (!ctx) throw new Error("Wrap your app with <NavigationProvider value={...} />")
  return ctx
}

// Optional thin wrappers your UI library can use directly
export const UiLink = forwardRef<HTMLAnchorElement, UiLinkProps>(function UiLink(p, ref) {
  const { Link } = useNavigation()
  // @ts-expect-error ref will be forwarded by adapter if supported
  return <Link ref={ref} {...p} />
})

export function UiImage(props: UiImageProps) {
  const { Image } = useNavigation()
  return <Image {...props} />
}
